/**
 * Hook to fetch and transform conversations for the sidebar navigation.
 * Provides infinite scroll support with pagination and separates pinned/unpinned conversations.
 */

import { useGetAgentConversationsQuery } from '@saaga/shared/store/api.store'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  buildConversationPinStateMap,
  type ConversationPinStateMap,
  filterPinnedItems,
  filterUnpinnedItems,
  mergeUniqueItems,
  transformConversationToNavItem,
} from './sidebar-conversations.helper'
import type { SidebarNavItem } from './sidebar-nav-item.interface'
import { useSidebarOrgReset } from './use-sidebar-org-reset.hook'

export type { ConversationPinStateMap } from './sidebar-conversations.helper'

const PAGE_SIZE = 50

interface UseSidebarConversationsReturn {
  pinnedConversationItemList: SidebarNavItem[]
  conversationItemList: SidebarNavItem[]
  conversationPinStateMap: ConversationPinStateMap
  isLoading: boolean
  isFetching: boolean
  hasMore: boolean
  loadMore: () => void
}

export const useSidebarConversations = (): UseSidebarConversationsReturn => {
  const [page, setPage] = useState(1)
  const [accumulatedItemList, setAccumulatedItemList] = useState<SidebarNavItem[]>([])
  useSidebarOrgReset(setPage, setAccumulatedItemList)

  const { data, isLoading, isFetching } = useGetAgentConversationsQuery({
    page,
    page_size: PAGE_SIZE,
  })

  const hasMore = useMemo(() => data?.has_more ?? false, [data?.has_more])

  useEffect(() => {
    if (!data?.items) return

    const newItemList = data.items.map(transformConversationToNavItem)

    if (page === 1) {
      setAccumulatedItemList(newItemList)
      return
    }

    setAccumulatedItemList((prev) => mergeUniqueItems(prev, newItemList))
  }, [data?.items, page])

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) setPage((prev) => prev + 1)
  }, [hasMore, isFetching])

  const pinnedConversationItemList = useMemo(
    () => filterPinnedItems(accumulatedItemList),
    [accumulatedItemList]
  )

  const conversationItemList = useMemo(
    () => filterUnpinnedItems(accumulatedItemList),
    [accumulatedItemList]
  )

  const conversationPinStateMap = useMemo(
    () => buildConversationPinStateMap(accumulatedItemList),
    [accumulatedItemList]
  )

  return {
    pinnedConversationItemList,
    conversationItemList,
    conversationPinStateMap,
    isLoading,
    isFetching,
    hasMore,
    loadMore,
  }
}
